import { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";

const BRAND_LOGOS = [
  { name: "Honda", src: "/brands/honda-white.png" },
  { name: "Yamaha", src: "/brands/yamaha-white.png" },
  { name: "Suzuki", src: "/brands/suzuki-white.png" },
  { name: "KTM", src: "/brands/ktm.png" },
  { name: "Bajaj", src: "/brands/bajaj-white.png" },
  { name: "Hero", src: "/brands/hero-white.png" },
  { name: "TVS", src: "/brands/tvs-white.png" },
  { name: "Loncin", src: "/brands/loncin-white.png" },
  { name: "Nipponia", src: "/brands/nipponia-white.png" },
  { name: "PGO", src: "/brands/pgo-white.png" },
  { name: "Super Tucan", src: "/brands/supertucan-white.png" },
  { name: "Super Gato", src: "/brands/supergato-white.png" },
  { name: "Voge", src: "/brands/voge.png" },
  { name: "Ducati", src: "/brands/ducati.png" },
  { name: "Toyota", src: "/brands/automobiles/toyota.png" },
  { name: "Honda Auto", src: "/brands/automobiles/hondaauto.png" },
  { name: "Nissan", src: "/brands/automobiles/nissan.png" },
  { name: "Mazda", src: "/brands/automobiles/mazda.png" },
  { name: "Ford", src: "/brands/automobiles/ford.png" },
  { name: "Chevrolet", src: "/brands/automobiles/chevrolet.png" },
  { name: "Hyundai", src: "/brands/automobiles/hyundai.png" },
  { name: "Kia", src: "/brands/automobiles/kia.png" },
  { name: "Volkswagen", src: "/brands/automobiles/volkswagen.png" },
  { name: "BMW", src: "/brands/automobiles/bmw.png" },
  { name: "Audi", src: "/brands/automobiles/audi.png" },
  { name: "Mercedes-Benz", src: "/brands/automobiles/mercedes.png" },
  { name: "Changan", src: "/brands/automobiles/changan.png" },
  { name: "Daihatsu", src: "/brands/automobiles/daihatsu.png" },
];

const SPEED = 0.6; // px per frame
const ITEM_WIDTH = 160; // gap between items in px

export function BrandsInfiniteCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const rafRef = useRef<number>(0);

  // Duplicamos para scroll infinito
  const items = [...BRAND_LOGOS, ...BRAND_LOGOS, ...BRAND_LOGOS];
  const totalWidthSingle = BRAND_LOGOS.length * ITEM_WIDTH;

  const applyProximityEffect = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const container = track.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;

    const children = track.children as HTMLCollectionOf<HTMLElement>;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const childRect = child.getBoundingClientRect();
      const childCenterX = childRect.left + childRect.width / 2;
      const distance = Math.abs(centerX - childCenterX);
      const maxDist = containerRect.width / 2;

      // 0 = center, 1 = edge
      const ratio = Math.min(distance / maxDist, 1);

      // Scale: 1.25 at center → 0.7 at edges
      const scale = 1.25 - ratio * 0.55;
      // Opacity: 1 at center → 0.15 at edges
      const opacity = 1 - ratio * 0.85;

      child.style.transform = `scale(${scale})`;
      child.style.opacity = `${opacity}`;
    }
  }, []);

  useEffect(() => {
    const animate = () => {
      const track = trackRef.current;
      if (!track) return;

      offsetRef.current -= SPEED;

      // Reset seamlessly when first set has scrolled out
      if (Math.abs(offsetRef.current) >= totalWidthSingle) {
        offsetRef.current += totalWidthSingle;
      }

      track.style.transform = `translateX(${offsetRef.current}px)`;
      applyProximityEffect();

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [totalWidthSingle, applyProximityEffect]);

  return (
    <div className="relative w-full overflow-hidden py-8">
      <div
        ref={trackRef}
        className="flex items-center will-change-transform"
        style={{ gap: `${ITEM_WIDTH - 90}px` }}
      >
        {items.map((brand, i) => (
          <Link
            key={`${brand.name}-${i}`}
            to={`/catalogo?q=${encodeURIComponent(brand.name)}`}
            className="flex h-24 w-24 shrink-0 items-center justify-center transition-none"
          >
            <img
              src={brand.src}
              alt={brand.name}
              className="h-full w-full object-contain brightness-0 invert drop-shadow-[0_2px_12px_rgba(255,255,255,0.15)]"
              loading="lazy"
              draggable={false}
            />
          </Link>
        ))}
      </div>
    </div>
  );
}
